
import {
  defineComponent, computed, ref, watch, nextTick, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { pickLocalizedText } from '@/utils/localized-text';
import type { WidgetCatalogEntry, WidgetLocalizedText } from '@/types/integrations';

interface WidgetCard {
  id: string;
  slug: string;
  key: string;
  name: string;
  description: string;
  preview: string;
  icon: string;
  intgName: string;
  intgIcon: string;
  installed: boolean;
  entry: WidgetCatalogEntry;
}

export default defineComponent({
  name: 'IntegrationWidgetsBrowserModal',

  props: {
    visible: { type: Boolean, default: false },
    catalog: { type: Array as PropType<WidgetCatalogEntry[]>, default: () => [] },
    locale: { type: String, default: 'fr' },
    // Gating marketplace ShopiMind : quand `false`, les CTA « installer » et le
    // lien « explorer la marketplace » deviennent « Bientôt disponible » (le
    // parent — WidgetsPanel — relaie l'état depuis le point de vérité routeur).
    marketplaceEnabled: { type: Boolean, default: false },
  },

  emits: ['update:visible', 'add', 'install', 'open-marketplace'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const selectedSlug = ref<string>('all');
    const search = ref('');

    // G15 — accessibilité modale : refs pour le focus initial + focus-trap.
    const overlayRef = ref<HTMLElement | null>(null);
    const modalRef = ref<HTMLElement | null>(null);

    const focusableSelector = [
      'a[href]', 'button:not([disabled])', 'input:not([disabled])',
      'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    const focusableEls = (): HTMLElement[] => {
      if (!modalRef.value) return [];
      return Array.from(modalRef.value.querySelectorAll<HTMLElement>(focusableSelector))
        .filter((el) => el.offsetParent !== null);
    };

    // Focus-trap : Tab depuis le dernier élément → premier, Shift+Tab depuis le
    // premier → dernier ; le focus ne peut pas sortir de la modale.
    const onTabKey = (e: KeyboardEvent) => {
      const els = focusableEls();
      if (els.length === 0) return;
      const first = els[0];
      const last = els[els.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || active === overlayRef.value)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    // Reset filters whenever the modal (re)opens + focus initial (a11y).
    watch(() => props.visible, (open) => {
      if (open) {
        selectedSlug.value = 'all';
        search.value = '';
        nextTick(() => {
          const els = focusableEls();
          (els[0] ?? overlayRef.value)?.focus();
        });
      }
    });

    const localize = (text?: WidgetLocalizedText): string =>
      pickLocalizedText(text as any, String(props.locale || 'fr'));

    // Partner integrations only. First-party (kind: 'shopimind') widgets are
    // surfaced in the editor's NATIVE widgets list (not here) — this in-editor
    // marketplace is reserved for partner apps.
    const integrationEntries = computed<WidgetCatalogEntry[]>(
      () => (props.catalog || []).filter((e) => e.kind === 'partner'),
    );

    const totalWidgets = computed(() => integrationEntries.value.reduce(
      (acc, e) => acc + (e.widgets?.length || 0), 0,
    ));

    const railIntegrations = computed(() => integrationEntries.value.map((e) => ({
      slug: e.integration_slug,
      name: e.integration_name,
      icon: e.integration_icon_url || '',
      initial: (e.integration_name || '?').charAt(0).toUpperCase(),
      installed: e.shop_integration_status === 'active',
      count: e.widgets?.length || 0,
    })));

    const visibleWidgets = computed<WidgetCard[]>(() => {
      const q = search.value.trim().toLowerCase();
      const cards: WidgetCard[] = [];
      integrationEntries.value.forEach((e) => {
        if (selectedSlug.value !== 'all' && e.integration_slug !== selectedSlug.value) return;
        (e.widgets || []).forEach((w) => {
          const name = localize(w.name) || w.key;
          const description = localize(w.description);
          if (q && !`${name} ${description} ${e.integration_name}`.toLowerCase().includes(q)) return;
          cards.push({
            id: `${e.integration_slug}__${w.key}`,
            slug: e.integration_slug,
            key: w.key,
            name,
            description,
            preview: w.preview_image_url || '',
            icon: w.icon_url || e.integration_icon_url || '',
            intgName: e.integration_name,
            intgIcon: e.integration_icon_url || '',
            installed: e.shop_integration_status === 'active',
            entry: e,
          });
        });
      });
      return cards;
    });

    const close = () => emit('update:visible', false);

    return {
      t,
      selectedSlug,
      search,
      totalWidgets,
      railIntegrations,
      visibleWidgets,
      close,
      overlayRef,
      modalRef,
      onTabKey,
    };
  },
});
